import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, TextField, Button, Paper, FormControlLabel, Checkbox,
    ToggleButton, ToggleButtonGroup, Autocomplete, Divider, Stack, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function AddCoin() {
    const navigate = useNavigate();
    const [mode, setMode] = useState('existing'); // Default to adding specimen to existing coin
    const [existingCoins, setExistingCoins] = useState([]);
    const [selectedExistingCoin, setSelectedExistingCoin] = useState(null);

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification({ ...notification, open: false });
    };

    const [formData, setFormData] = useState({
        pcgs_id: '',
        country: '',
        denomination: '',
        series_name: '',
        series_date: '',
        type: '',
        date: '',
        major: '',
        minor: '',
        mint: '',
        status: 'have',
        // Specimen fields
        grade: '',
        grader: '',
        comments: '',
        purchase_price: '',
        pcgs_value: '',
        date_of_purchase: new Date().toISOString().split('T')[0],
        storage_location: '',
        duplicate: false
    });

    useEffect(() => {
        // Fetch existing coins for Autocomplete
        axios.get(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins`)
            .then(res => setExistingCoins(res.data))
            .catch(err => console.error("Error loading coins:", err));
    }, []);

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setMode(newMode);
        }
    };

    const handleExistingCoinSelect = (event, newValue) => {
        setSelectedExistingCoin(newValue);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Check if specimen data is provided
        const hasSpecimenData = formData.grade || formData.purchase_price || formData.grader;

        let newSpecimen = null;

        if (hasSpecimenData || mode === 'existing') {
            newSpecimen = {
                id: Date.now(), // Generate a temporary ID
                grade: formData.grade,
                grader: formData.grader,
                comments: formData.comments,
                source_id: -1,
                purchase_price: parseFloat(formData.purchase_price) || 0,
                pcgs_value: parseFloat(formData.pcgs_value) || 0,
                date_of_purchase: formData.date_of_purchase,
                storage_location: formData.storage_location,
                duplicate: formData.duplicate
            };
        }

        if (mode === 'existing') {
            if (!selectedExistingCoin) {
                showNotification("Please select an existing coin.", 'warning');
                return;
            }

            if (!newSpecimen) {
                newSpecimen = {
                    id: Date.now(),
                    grade: '',
                    grader: '',
                    purchase_price: 0,
                    pcgs_value: 0
                };
            }

            // Add specimen to existing coin
            const updatedCoin = {
                ...selectedExistingCoin,
                specimens: [...(selectedExistingCoin.specimens || []), newSpecimen]
            };

            const updateId = selectedExistingCoin.id || selectedExistingCoin.pcgs_id;

            axios.put(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins/${updateId}`, updatedCoin)
                .then(() => {
                    showNotification('Specimen added to existing coin successfully!', 'success');
                    setTimeout(() => navigate('/'), 1500);
                })
                .catch(err => {
                    console.error('Error updating coin:', err);
                    showNotification('Failed to update coin.', 'error');
                });

        } else {
            // Validate PCGS ID
            const pcgsIdInt = parseInt(formData.pcgs_id);
            if (!pcgsIdInt || isNaN(pcgsIdInt)) {
                showNotification("Please enter a valid numeric PCGS ID.", 'warning');
                return;
            }

            // Create new coin type
            const newCoinPayload = {
                pcgs_id: pcgsIdInt,
                country: formData.country,
                denomination: formData.denomination,
                series_name: formData.series_name,
                series_date: formData.series_date,
                type: formData.type,
                date: formData.date,
                major: formData.major,
                minor: formData.minor,
                mint: formData.mint,
                status: formData.status,
                specimens: newSpecimen ? [newSpecimen] : [] // Allow empty specimens
            };

            axios.post(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins`, newCoinPayload)
                .then(() => {
                    showNotification('New coin type added successfully!', 'success');
                    setTimeout(() => navigate('/'), 1500);
                })
                .catch(err => {
                    console.error('Error adding coin:', err);
                    console.error('Server Error Details:', err.response?.data);
                    if (err.response && err.response.status === 409) {
                        showNotification("A coin with this PCGS ID already exists.", 'error');
                    } else {
                        showNotification(`Failed to add coin. Server says: ${JSON.stringify(err.response?.data || err.message)}`, 'error');
                    }
                });
        }
    };

    return (
        <Container maxWidth="md">
            <Paper sx={{ p: 4, mt: 4 }}>
                <Typography variant="h5" gutterBottom align="center">Add Coin to Inventory</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                    >
                        <ToggleButton value="existing">Add Specimen (Existing Type)</ToggleButton>
                        <ToggleButton value="new">Add New Coin Type</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>

                        {/* === MODE: EXISTING COIN === */}
                        {mode === 'existing' && (
                            <Box>
                                <Autocomplete
                                    options={existingCoins}
                                    getOptionLabel={(option) => `${option.date} ${option.denomination} ${option.series_date ? option.series_date + ' ' : ''}${option.series_name} (PCGS: ${option.pcgs_id})`}
                                    value={selectedExistingCoin}
                                    onChange={handleExistingCoinSelect}
                                    renderInput={(params) => <TextField {...params} label="Search Existing Coin Type" fullWidth />}
                                />
                                {selectedExistingCoin && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                        <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                                            <Typography variant="body2"><strong>Series:</strong> {selectedExistingCoin.series_date ? `${selectedExistingCoin.series_date} ` : ''}{selectedExistingCoin.series_name}</Typography>
                                            <Typography variant="body2"><strong>Major:</strong> {selectedExistingCoin.major}</Typography>
                                            <Typography variant="body2"><strong>Mint:</strong> {selectedExistingCoin.mint}</Typography>
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* === MODE: NEW COIN === */}
                        {mode === 'new' && (
                            <>
                                <Typography variant="h6" sx={{ color: 'text.secondary', borderBottom: '1px solid #eee', pb: 1 }}>
                                    New Coin Type Details
                                </Typography>

                                <TextField fullWidth label="PCGS ID" name="pcgs_id" value={formData.pcgs_id} onChange={handleChange} required />

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField fullWidth label="Country" name="country" value={formData.country} onChange={handleChange} />
                                    <TextField fullWidth label="Denomination" name="denomination" value={formData.denomination} onChange={handleChange} required />
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField fullWidth label="Date" name="date" value={formData.date} onChange={handleChange} required />
                                    <TextField fullWidth label="Mint" name="mint" value={formData.mint} onChange={handleChange} />
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField fullWidth label="Series Name" name="series_name" value={formData.series_name} onChange={handleChange} />
                                    <TextField fullWidth label="Series Date" name="series_date" value={formData.series_date} onChange={handleChange} />
                                </Stack>
                                <TextField fullWidth label="Type" name="type" value={formData.type} onChange={handleChange} />

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField fullWidth label="Major Variety" name="major" value={formData.major} onChange={handleChange} />
                                    <TextField fullWidth label="Minor Variety" name="minor" value={formData.minor} onChange={handleChange} />
                                </Stack>

                                <TextField fullWidth label="Status" name="status" value={formData.status} onChange={handleChange} />
                            </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* === SPECIMEN DETAILS (COMMON) === */}
                        <Typography variant="h6" sx={{ color: 'text.secondary', borderBottom: '1px solid #eee', pb: 1 }}>
                            Specimen Details {mode === 'new' && <Typography component="span" variant="caption">(Optional)</Typography>}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth label="Grade" name="grade" value={formData.grade} onChange={handleChange} />
                            <TextField fullWidth label="Grader" name="grader" value={formData.grader} onChange={handleChange} />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth label="Purchase Price" name="purchase_price" type="number" value={formData.purchase_price} onChange={handleChange} />
                            <TextField fullWidth label="PCGS Value" name="pcgs_value" type="number" value={formData.pcgs_value} onChange={handleChange} />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth label="Date of Purchase" name="date_of_purchase" type="date" InputLabelProps={{ shrink: true }} value={formData.date_of_purchase} onChange={handleChange} />
                            <TextField fullWidth label="Storage Location" name="storage_location" value={formData.storage_location} onChange={handleChange} />
                        </Stack>

                        <TextField fullWidth multiline rows={2} label="Comments" name="comments" value={formData.comments} onChange={handleChange} />

                        <FormControlLabel control={<Checkbox checked={formData.duplicate} onChange={handleChange} name="duplicate" />} label="Duplicate" />

                        <Box sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained" color="primary" size="large" fullWidth>
                                {mode === 'existing' ? 'Add Specimen to Coin' : 'Create New Coin Entry'}
                            </Button>
                        </Box>

                    </Stack>
                </form>
            </Paper>

            <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification}>
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container >
    );
}
